import React, { useEffect, useRef, useState } from "react";

/**
 * Count-up animation that fires when the element scrolls into view.
 * Designed for the Quick Facts band ("28+ Years", "1,500+ Roofs",
 * "155+ Reviews", "10 yr Warranty") — 2026 modernization brief.
 *
 * Accepts a string `value` (e.g. "28+", "1,500+", "5.0", "10 yr") so the
 * caller does not have to deconstruct number-vs-suffix logic.
 */
function parseValue(value) {
  const m = String(value).match(/^([\d.,]+)(.*)$/);
  if (!m) return { num: null, suffix: "", hasDecimal: false };
  const numStr = m[1].replace(/,/g, "");
  return { num: parseFloat(numStr), suffix: m[2], hasDecimal: numStr.includes(".") };
}

function formatValue(num, parsed) {
  if (parsed.hasDecimal) return num.toFixed(1) + parsed.suffix;
  if (parsed.num >= 1000) return Math.round(num).toLocaleString("en-US") + parsed.suffix;
  return Math.round(num).toString() + parsed.suffix;
}

export function CountUp({ value, duration = 1200, className = "", testId }) {
  const ref = useRef(null);
  const [display, setDisplay] = useState(value);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const parsed = parseValue(value);
    if (parsed.num == null) return;
    if (typeof IntersectionObserver === "undefined") return;
    let raf = 0;
    let animationStart = 0;
    const animate = () => {
      const tick = (now) => {
        if (!animationStart) animationStart = now;
        const t = Math.min(1, (now - animationStart) / duration);
        const eased = 1 - Math.pow(1 - t, 3);
        setDisplay(formatValue(parsed.num * eased, parsed));
        if (t < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    };
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            animate();
            obs.disconnect();
          }
        }
      },
      { threshold: 0.4 },
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [value, duration]);

  return (
    <span ref={ref} className={className} data-testid={testId}>
      {display}
    </span>
  );
}
